import { useEffect, useRef, useState } from "react";
import { Icon } from "../icons/Icon";
import { Button } from "../primitives/Button";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const DOW = ["L", "M", "X", "J", "V", "S", "D"];
const isoDay = (y: number, m: number, d: number) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** «30 de septiembre de 2026»: lo que lee el campo y el nombre de cada día. */
export function fechaLarga(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d ?? ""} de ${MESES[(m ?? 1) - 1] ?? ""} de ${y ?? ""}`;
}

export interface CalendarProps {
  /** Día elegido (YYYY-MM-DD). */
  value: string;
  onPick: (iso: string) => void;
  /** Hoy (YYYY-MM-DD): se marca con `sk-today`. */
  today: string;
  /** Último día elegible: los posteriores se ven pero no se eligen (con el porqué en su nombre). */
  max?: string | undefined;
  maxReason?: string | undefined;
  label: string;
  id?: string | undefined;
}

/**
 * Calendario del sistema (`sk-calendar`): cabecera con el mes y sus
 * flechas, la rejilla con los días de la semana y los del mes; los del mes
 * anterior, apagados (`sk-out`). Un día fuera de rango sigue a la vista con
 * `aria-disabled` y el motivo en su nombre: no desaparece sin explicación.
 * Cada día es un botón; el elegido lleva `aria-pressed` (un `aria-selected`
 * sobre un botón sin rol de opción no lo lee nadie).
 */
export function Calendar({ value, onPick, today, max, maxReason, label, id }: CalendarProps) {
  // sin día elegido (`""`), el calendario abre en el mes de hoy
  const [y0, m0] = (value !== "" ? value : today).split("-").map(Number);
  const [view, setView] = useState({ y: y0 ?? 2026, m: (m0 ?? 1) - 1 });
  const grid = useRef<HTMLDivElement>(null);
  // al abrir, el foco cae en el día elegido (como el listbox del Select)
  useEffect(() => { (grid.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]') ?? grid.current?.querySelector<HTMLButtonElement>(".sk-today"))?.focus(); }, []);
  const lead = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const days = new Date(view.y, view.m + 1, 0).getDate();
  const prevDays = new Date(view.y, view.m, 0).getDate();
  const step = (n: number) => setView((v) => { const m = v.m + n; return { y: v.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 }; });
  const mes = MESES[view.m] ?? "";
  return (
    <div className="sk-calendar" role="dialog" aria-label={label} id={id}>
      <div className="sk-cal-head">
        <Button variant="ghost" size="s" iconOnly aria-label="Mes anterior" onClick={() => step(-1)}>‹</Button>
        <b>{`${mes.charAt(0).toUpperCase()}${mes.slice(1)} ${view.y}`}</b>
        <Button variant="ghost" size="s" iconOnly aria-label="Mes siguiente" onClick={() => step(1)}>›</Button>
      </div>
      <div className="sk-cal-grid" ref={grid}>
        {DOW.map((d) => <span className="sk-cal-dow" key={d}>{d}</span>)}
        {Array.from({ length: lead }, (_, i) => <span className="sk-cal-day sk-out" aria-hidden="true" key={`o${i}`}>{prevDays - lead + i + 1}</span>)}
        {Array.from({ length: days }, (_, i) => {
          const d = isoDay(view.y, view.m, i + 1);
          const fuera = max !== undefined && d > max;
          return (
            <button
              type="button" key={d} className={d === today ? "sk-cal-day sk-today" : "sk-cal-day"}
              aria-pressed={d === value} aria-label={`${fechaLarga(d)}${fuera && maxReason !== undefined ? ` (${maxReason})` : ""}`}
              {...(fuera && { "aria-disabled": true as const, tabIndex: -1 })}
              onClick={() => { if (!fuera) onPick(d); }}
            >{i + 1}</button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Campo de fecha del sistema (`sk-input--date` en su `sk-inputwrap`): un
 * mando de solo lectura que abre el calendario como popover. Se abre con
 * clic, Enter, espacio o flecha abajo; Escape o un clic fuera lo cierran y
 * el foco vuelve al campo. El popover (`sk-calpop`, pegado bajo el campo y
 * sobre lo demás) es geometría del propio mando: clase del sistema, no
 * estilo en línea. `aria-controls` solo apunta al calendario cuando existe.
 * `value=""`: sin fecha todavía; se lee `placeholder`. `stale`: apagado
 * (sin conexión), fuera del teclado y sin abrir.
 */
export function DateField({ id, value, onChange, today, max, maxReason, labelledBy, describedBy, calendarLabel, placeholder, stale }: {
  id: string; value: string; onChange: (iso: string) => void; today: string;
  max?: string | undefined; maxReason?: string | undefined; labelledBy?: string | undefined; describedBy?: string | undefined; calendarLabel: string;
  placeholder?: string | undefined; stale?: boolean | undefined;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); input.current?.focus(); } };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <div ref={box} className="sk-datefield">
      <div className="sk-inputwrap">
        <Icon name="chevron" />
        <input
          ref={input} id={id} className="sk-input sk-input--date" readOnly value={value === "" ? "" : fechaLarga(value)} placeholder={placeholder}
          aria-labelledby={labelledBy} aria-describedby={describedBy} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? `${id}-cal` : undefined}
          {...(stale === true && { "aria-disabled": true as const, tabIndex: -1 })}
          onClick={() => { if (stale !== true) setOpen((o) => !o); }}
          onKeyDown={(e) => { if (stale !== true && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) { e.preventDefault(); setOpen(true); } }}
        />
      </div>
      {open && (
        <div className="sk-calpop">
          <Calendar id={`${id}-cal`} value={value} today={today} max={max} maxReason={maxReason} label={calendarLabel}
            onPick={(d) => { onChange(d); setOpen(false); input.current?.focus(); }} />
        </div>
      )}
    </div>
  );
}
